import { useCallback, useEffect, useRef } from 'react';

// Remember where the page was scrolled to before drilling into something, and
// put the reader back there when they come out.
//
// ⚠️ THE PROBLEM IS THAT THE LIST IS NOT THERE WHEN YOU ASK FOR IT BACK. Going
// back unmounts the detail screen and remounts the list, which then fetches its
// rows — so for a frame or two the document is a spinner two hundred pixels
// tall and `window.scrollTo(0, 1400)` silently clamps to the bottom of that,
// which is 0. Restoring once, immediately, looks like it works on a fast local
// network and fails on every real one.
//
// So the restore RETRIES across frames until the document is actually tall
// enough to hold the position, and gives up after a short budget rather than
// spinning for ever on a list that came back shorter (a filter cleared, rows
// deleted). Same shape as `components/chat/useScrollBackLoader.js`, which
// solves the mirror image of this for prepended chat history.
//
// ⚠️ IT ALSO STOPS ON ANY HUMAN SCROLL. If the reader has started scrolling
// before the rows land, they have said where they want to be and a restore
// firing a moment later would yank the page out from under them.

const MAX_FRAMES = 60; // ~1s at 60fps: past this, the list is not coming back.

export default function useScrollRestore() {
  const savedRef = useRef(0);
  const pendingRef = useRef(false);
  const rafRef = useRef(null);

  const cancel = useCallback(() => {
    pendingRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  /** Call as you leave the list. */
  const save = useCallback(() => {
    savedRef.current = window.scrollY;
    // The detail screen is a different document; it opens at the top, the way
    // arriving anywhere new does.
    window.scrollTo(0, 0);
  }, []);

  /** Call as you come back to it. */
  const restore = useCallback(() => {
    const target = savedRef.current;
    if (!target) return;
    pendingRef.current = true;

    let frames = 0;
    const step = () => {
      if (!pendingRef.current) return;
      frames += 1;

      const reachable = document.documentElement.scrollHeight - window.innerHeight;
      if (reachable >= target) {
        window.scrollTo(0, target);
        cancel();
        return;
      }
      if (frames >= MAX_FRAMES) {
        // Best effort: as close as this document can get.
        window.scrollTo(0, Math.max(0, reachable));
        cancel();
        return;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }, [cancel]);

  // A deliberate scroll by the reader outranks a restore still in flight.
  useEffect(() => {
    const onWheel = () => { if (pendingRef.current) cancel(); };
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchmove', onWheel, { passive: true });
    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchmove', onWheel);
      cancel();
    };
  }, [cancel]);

  return { save, restore };
}
